import React from 'react';
import { Box, Card, CardContent, Typography, Divider } from '@mui/material';
import { ExperimentWindow } from '../../types/experiment.types';

interface WindowsTimelineProps {
  windows: ExperimentWindow[];
  compact?: boolean;
}

const WindowsTimeline: React.FC<WindowsTimelineProps> = ({ windows, compact = false }) => {
  const formatDate = (date: string) => new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  const getDaysDifference = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  };

  if (compact) {
    return (
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {windows.map((window, idx) => (
          <Box key={idx} sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
              {window.name}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {formatDate(window.startDate)} → {formatDate(window.endDate)}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'primary.main' }}>
              {getDaysDifference(window.startDate, window.endDate)} days
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Measurement Windows
        </Typography>
        {windows.map((window, idx) => (
          <Box key={idx}>
            {idx > 0 && <Divider sx={{ my: 2 }} />}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  {window.name}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  {formatDate(window.startDate)} — {formatDate(window.endDate)}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>Period</Typography>
                    <Typography variant="body2">{getDaysDifference(window.startDate, window.endDate)} days</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>Method</Typography>
                    <Typography variant="body2">{window.method}</Typography>
                  </Box>
                  {window.metadata?.seasonalityControl && (
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>Seasonality Control</Typography>
                      <Typography variant="body2">Enabled</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </CardContent>
    </Card>
  );
};

export default WindowsTimeline;
